import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { EmptyContent } from '@/components/ui';
import { IconFolderSearch } from '@tabler/icons-react';

interface FileDropzoneProps {
  onFilesAdded: (files: File[]) => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFilesAdded,
  children,
  disabled = false,
  className,
  style,
}) => {
  // style is reserved for future layout customization; keep prop without changing render output.
  void style;
  const [dragActive, setDragActive] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFilesAdded(acceptedFiles);
    },
    [onFilesAdded]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled,
    // @ts-expect-error webkitdirectory is non-standard but needed for folder upload
    webkitdirectory: 'true',
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: () => setDragActive(false),
    onDropRejected: () => setDragActive(false),
  });

  const showDropOverlay = dragActive || isDragActive;

  return (
    <div
      {...getRootProps()}
      className={className}
      style={{
        position: 'relative',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,

        // ...style,
      }}
    >
      {/* Drop overlay */}
      {showDropOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 102,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage:
              'linear-gradient(to bottom, var(--primary-lightest), var(--primary-light))',
            borderRadius: 'var(--radius-3)',
          }}
        >
          <EmptyContent
            withIconBox
            icon={<IconFolderSearch />}
            title="Drop or paste your data here"
            description="Drop your files here. We accept up to 2GB per file."
            size="large"
            disableAnimation
          />
        </div>
      )}

      {/* Hidden file input for dropzone */}
      <input {...getInputProps()} style={{ display: 'none' }} />

      {/* Content */}
      {children}
    </div>
  );
};
